"use client";
import { useEffect, useRef, useState } from "react";
import { useProductSummaries } from "@/hooks/useProductSummaries";
import { useRecentlyViewedModuleConfig } from "@/monarch/rules/recentlyViewedModule/useRecentlyViewedModuleConfig";
import RecentlyViewedProductCardListClient from "./RecentlyViewedProductCardListClient";
import type { RecentlyViewedProductCardListProps } from "./types";
import useTranslations from "@/i18n/useTranslations";

const DEFAULT_MAX_ITEMS = 10;
const DEFAULT_MAX_ROWS = 2;
const DEFAULT_INITIAL_CARDS_LENGTH = 4;
const LOADING_TIMEOUT_MS = 5000; // bail out after 5 seconds

/**
 * Fetches summaries + filters; `useMonarchRule` is not used here so hooks stay valid when the
 * outer wrapper returns early during Monarch/flag loading (EDUN-10032).
 */
function RecentlyViewedProductCardListInner({
  currentProductId,
  maxItems,
  displayLimit,
  minItemsToRender,
  title,
  filterByPartner,
  recencyDays,
  initialCardsLength = DEFAULT_INITIAL_CARDS_LENGTH,
  maxRows = DEFAULT_MAX_ROWS,
  ...props
}: RecentlyViewedProductCardListProps & {
  maxItems: number;
  minItemsToRender: number;
  title: string;
  recencyDays: number;
  maxRows: number;
  initialCardsLength: number;
}) {
  const {
    products: allProducts,
    isLoading,
    error,
  } = useProductSummaries({
    limit: maxItems,
  });

  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoading) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return; // don't reset loadingTimedOut here
    }
    setLoadingTimedOut(false); // reset when a NEW load starts
    timerRef.current = setTimeout(() => {
      setLoadingTimedOut(true);
    }, LOADING_TIMEOUT_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading]);

  let products = currentProductId
    ? allProducts.filter((p) => p.productUuid !== currentProductId)
    : allProducts;

  // Filter to items viewed within the last recencyDays days
  const oneDayInMs = 24 * 60 * 60 * 1000;
  const recencyCutoff = Date.now() - recencyDays * oneDayInMs;
  products = products.filter((p) => p.viewedAt >= recencyCutoff);

  if (filterByPartner) {
    const needle = filterByPartner.toLowerCase();
    products = products.filter((p) => {
      const names = Array.isArray(p.partnerName)
        ? p.partnerName
        : [p.partnerName];
      const keys = p.partnerKeys ?? [];
      const partners = Array.isArray(p.partner)
        ? p.partner
        : p.partner
          ? [p.partner]
          : [];
      return [...names, ...keys, ...partners].some((v) =>
        v?.toLowerCase().includes(needle),
      );
    });
  }

  // Cap the final result independently of the API fetch limit
  if (displayLimit !== undefined) {
    products = products.slice(0, displayLimit);
  }

  // Still fetching and haven't timed out — don't render yet
  if (isLoading && !loadingTimedOut && products.length < minItemsToRender) {
    return null;
  }
  // Fetch done (or timed out) with nothing useful to show
  if (
    (!isLoading || loadingTimedOut) &&
    (error || products.length < minItemsToRender)
  ) {
    return null;
  }

  return (
    <RecentlyViewedProductCardListClient
      {...props}
      initialCardsLength={initialCardsLength}
      maxRows={maxRows}
      title={title}
      products={products}
    />
  );
}

/**
 * Recently Viewed module. When `NEXT_PUBLIC_FEATURE_RECENTLY_VIEWED_PRODUCTS` is `test`, tunables
 * are loaded from Monarch rule `recentlyViewedProducts` (Monarch > shortcode/template props
 * > in-code defaults). Placement remains with CMS (shortcode) or PDP templates; pass `surface`
 * so `homepageEnabled` / `pdpEnabled` apply correctly.
 */
export default function RecentlyViewedProductCardList({
  currentProductId,
  maxItems = DEFAULT_MAX_ITEMS,
  displayLimit,
  minItemsToRender = 3,
  title,
  filterByPartner,
  recencyDays = 14,
  surface = "homepage",
  initialCardsLength = DEFAULT_INITIAL_CARDS_LENGTH,
  maxRows = DEFAULT_MAX_ROWS,
  ...rest
}: RecentlyViewedProductCardListProps) {
  const t = useTranslations("Common");
  const resolvedTitle = title ?? t("recentlyViewed");
  const { isLoading, isSurfaceAllowed, displayFromMonarch } =
    useRecentlyViewedModuleConfig(surface, {
      minItemsToRender,
      maxItems,
      displayLimit,
      recencyDays,
      title: resolvedTitle,
      maxRows,
      initialCardsLength,
    });

  if (isLoading || !isSurfaceAllowed) {
    return null;
  }

  const effective = displayFromMonarch ?? {
    minItemsToRender,
    maxItems,
    displayLimit,
    recencyDays,
    title: resolvedTitle,
    maxRows,
    initialCardsLength,
  };

  return (
    <RecentlyViewedProductCardListInner
      {...rest}
      currentProductId={currentProductId}
      displayLimit={effective.displayLimit}
      filterByPartner={filterByPartner}
      maxItems={effective.maxItems}
      maxRows={effective.maxRows}
      minItemsToRender={effective.minItemsToRender}
      recencyDays={effective.recencyDays}
      title={effective.title ?? resolvedTitle}
      initialCardsLength={effective.initialCardsLength}
    />
  );
}
